import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  AlertCircle,
  AlertTriangle,
  Briefcase,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  MapPin,
  RefreshCw,
  Search,
  Share2,
  Target,
  Users,
  X
} from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { enlaceDeBases, whatsappShareUrl } from '../services/convocatoriaShare';
import { normalizarClave, obtenerLogos } from '../services/entidadLogos';
import LogoEntidad from '../components/LogoEntidad';
import './trabaja-estado35.scss';

// Convocatorias de empleo público (CAS / D.Leg. 1057, 276, 728…).
// La tabla la llena un proceso externo con la service_role; el portal solo lee
// (política de lectura en supabase/migrations/202610310001_trabajos_estado_lectura.sql).
const TABLA = 'trabajos_estado';

// Supabase corta cada consulta en 1000 filas, así que se trae por lotes. La
// tabla no tiene clave primaria ni columna `id`: lo más estable para paginar es
// ordenar por la convocatoria y el puesto.
const BATCH_SIZE = 1000;
const ORDEN_ESTABLE = ['numero_convocatoria', 'puesto', 'remuneracion'];

// Solo las columnas que pinta el listado. Los cinco campos del perfil son el
// 65 % del peso de la tabla (1,1 MB por lote con `select('*')` contra 380 KB
// con esta lista, medido sobre 4.757 filas) y únicamente se leen al abrir el
// panel de detalle, así que se piden ahí.
const COLUMNAS_LISTA = [
  'puesto',
  'entidad',
  'ubicacion',
  'numero_convocatoria',
  'cantidad_vacantes',
  'remuneracion',
  'fecha_inicio',
  'fecha_fin',
  'enlace_detalle',
  'codigo_oferta'
].join(',');

const COLUMNAS_PERFIL = 'formacion,experiencia,especializacion,conocimiento,competencias';

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutos
const POR_PAGINA = 20;

// Lado del logo de la entidad en la fila. El mockup lo pone en 40, pero ahí un
// logo institucional horizontal (escudo + texto al lado) queda en una franja de
// ~12 px de alto y no se reconoce; a 56 sí se lee y la fila apenas crece,
// porque la columna del puesto ya mide dos líneas.
const LADO_LOGO = 56;

// Caché a nivel de módulo, como en el resto de las pantallas de convocatorias:
// volver a entrar a la sección no vuelve a bajar todo.
//
// `promesa` guarda la descarga en vuelo, y es lo que evita que la pantalla se
// quede colgada: antes había una bandera `isLoading` y el segundo montaje se
// iba sin hacer nada ni enterarse del resultado. Con 33 filas no se notaba;
// con 4.757 la descarga dura segundos, y bastaba salir de la sección y volver
// dentro de esa ventana para quedarse mirando el esqueleto para siempre.
let globalDataCache = {
  data: null,
  timestamp: null,
  promesa: null
};

// La caché vive en el módulo y sobrevive a cada montaje, así que las pruebas
// necesitan vaciarla entre casos para que un escenario no herede los datos del
// anterior. En la app nadie la llama: el botón «Actualizar» ya hace lo mismo.
export const vaciarCacheParaPruebas = () => {
  globalDataCache = { data: null, timestamp: null, promesa: null };
};

// ==========================================================
// Helpers
// ==========================================================
const two = (n) => String(n).padStart(2, '0');

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];

// Las fechas llegan como texto en formato peruano (29/10/2026). Se aceptan
// también ISO y guiones, por si la carga cambia de formato.
const parseFecha = (valor) => {
  const texto = String(valor || '').trim();
  if (!texto || texto === '-') return null;

  const dmy = texto.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    const fecha = new Date(Number(y), Number(m) - 1, Number(d));
    return isNaN(fecha.getTime()) ? null : fecha;
  }

  const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, y, m, d] = iso;
    const fecha = new Date(Number(y), Number(m) - 1, Number(d));
    return isNaN(fecha.getTime()) ? null : fecha;
  }

  const fecha = new Date(texto);
  return isNaN(fecha.getTime()) ? null : fecha;
};

// «2 nov», como el mockup.
const fmtDiaMes = (d) => (d ? `${d.getDate()} ${MESES_CORTOS[d.getMonth()]}` : '—');
const fmtFecha = (d) => (d ? `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}` : '—');

const hoyAMedianoche = () => {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
};

// Días que faltan para el cierre de postulación. Negativo = ya venció.
const diasHastaCierre = (fin) => {
  if (!fin) return null;
  return Math.round((fin - hoyAMedianoche()) / 86400000);
};

// Urgencia del mockup: naranja ≤3 días, amarillo ≤7, verde después. No es la
// escala del resto de la app (rojo ≤1, ámbar ≤7, gris): en una bolsa de empleo
// el umbral que importa es «me quedan tres días para postular».
const urgenciaDe = (dias) => {
  if (dias === null) return { nivel: 'lejos', label: 'Sin plazo' };
  if (dias < 0) return { nivel: 'cerrada', label: 'Plazo vencido' };
  if (dias === 0) return { nivel: 'pronto', label: 'Cierra hoy' };
  if (dias === 1) return { nivel: 'pronto', label: 'Cierra mañana' };
  if (dias <= 3) return { nivel: 'pronto', label: `Cierra en ${dias} días` };
  if (dias <= 7) return { nivel: 'semana', label: `Cierra en ${dias} días` };
  return { nivel: 'lejos', label: `Cierra en ${dias} días` };
};

// Porcentaje del plazo ya consumido, para la barra de la fila y del panel.
const avancePlazo = (inicio, fin, dias) => {
  if (!fin) return 0;
  if (dias !== null && dias < 0) return 100;
  if (!inicio) return 0;
  const total = Math.round((fin - inicio) / 86400000);
  if (total <= 0) return 100;
  return Math.max(0, Math.min(100, Math.round(((total - dias) / total) * 100)));
};

// «S/. 7,364.19» → 7364.19. Devuelve null cuando no hay monto legible (hay
// convocatorias que ponen «según escala» o lo dejan vacío).
//
// El símbolo de moneda se quita ANTES de buscar el número: si solo se filtran
// los caracteres no numéricos, el punto de «S/.» queda pegado al inicio y
// «S/. 12,500.00» se lee como 0.125.
const montoNumero = (valor) => {
  const encontrado = String(valor || '')
    .replace(/s\s*\/\s*\.?/i, ' ')
    .match(/\d[\d.,]*/);
  if (!encontrado) return null;

  let crudo = encontrado[0].replace(/[.,]$/, '');
  if (crudo.includes(',') && crudo.includes('.')) {
    // Formato peruano: coma para los miles, punto para los decimales.
    crudo = crudo.replace(/,/g, '');
  } else if (crudo.includes(',')) {
    // Solo coma: es decimal cuando deja dos dígitos al final («2900,50»);
    // si no, son miles («12,500»).
    crudo = /,\d{2}$/.test(crudo) ? crudo.replace(',', '.') : crudo.replace(/,/g, '');
  }

  const num = parseFloat(crudo);
  return Number.isFinite(num) && num > 0 ? num : null;
};

const fmtSoles = (num) =>
  num === null || num === undefined
    ? '—'
    : `S/ ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtSolesCorto = (num) =>
  num === null || num === undefined ? '—' : `S/ ${Math.round(num).toLocaleString('en-US')}`;

const fmtMiles = (num) => Number(num || 0).toLocaleString('en-US');

// Los puestos y las entidades llegan en mayúsculas sostenidas y así son
// pesados de leer («ARQUEÓLOGO/A RESIDENTE EN LA ZONA ARQUEOLÓGICA…»). Estas
// dos listas son lo que separa un título legible de un «Especialista En
// Arqueología» o un «Zona Registral N Ii».
const PALABRAS_MENORES = new Set([
  'de', 'del', 'la', 'las', 'el', 'los', 'en', 'y', 'e', 'o', 'u', 'a', 'al',
  'para', 'por', 'con', 'sin', 'sobre', 'ante', 'tras', 'que'
]);
const ROMANOS = /^(?:i{1,3}|iv|v|vi{1,3}|ix|x{1,3})$/;

const capitalizar = (texto) => {
  const base = String(texto || '').trim();
  if (!base) return '';
  // Un texto que ya viene en mixto se respeta tal cual.
  if (base !== base.toUpperCase()) return base;

  return base
    .toLowerCase()
    // El separador se captura para devolver los espacios originales.
    .split(/(\s+)/)
    .map((token, i) => {
      if (!token || /^\s+$/.test(token)) return token;
      const soloLetras = token.replace(/[^a-záéíóúüñ]/g, '');
      if (!soloLetras) return token;
      // «ZONA REGISTRAL N II» conserva el romano.
      if (ROMANOS.test(soloLetras)) return token.toUpperCase();
      if (i > 0 && PALABRAS_MENORES.has(soloLetras)) return token;
      // Solo la primera letra, y sin tocar lo que va tras una barra:
      // «arqueólogo/a» queda «Arqueólogo/a», no «Arqueólogo/A».
      return token.replace(/[a-záéíóúüñ]/, (letra) => letra.toUpperCase());
    })
    .join('');
};

// Del número de convocatoria salen el régimen y la modalidad:
// «D.LEG 1057 - DETERMINADO (SUPLENCIA)-60» → D.Leg. 1057 · Suplencia
const datosConvocatoria = (numero) => {
  const texto = String(numero || '').trim();
  if (!texto) return { regimen: null, modalidad: null, claseRegimen: 'otro' };

  let regimen = null;
  let claseRegimen = 'otro';
  if (/1057/.test(texto)) { regimen = 'D.Leg. 1057'; claseRegimen = '1057'; }
  else if (/728/.test(texto)) { regimen = 'D.Leg. 728'; claseRegimen = '728'; }
  else if (/276/.test(texto)) { regimen = 'D.Leg. 276'; claseRegimen = '276'; }
  else if (/30057|SERVIR/i.test(texto)) regimen = 'Ley SERVIR';
  else if (/PRACTIC|PRÁCTIC/i.test(texto)) regimen = 'Prácticas';

  const entre = texto.match(/\(([^)]+)\)/);
  return { regimen, modalidad: entre ? capitalizar(entre[1]) : null, claseRegimen };
};

// «LIMA - LA VICTORIA» → departamento + provincia/distrito
const partesUbicacion = (valor) => {
  const partes = String(valor || '')
    .split('-')
    .map((p) => p.trim())
    .filter(Boolean);
  if (partes.length === 0) return { departamento: '', detalle: '' };
  return { departamento: partes[0], detalle: partes.slice(1).join(' · ') };
};

const mediana = (nums) => {
  if (!nums.length) return null;
  const orden = [...nums].sort((a, b) => a - b);
  const mitad = Math.floor(orden.length / 2);
  return orden.length % 2 ? orden[mitad] : (orden[mitad - 1] + orden[mitad]) / 2;
};

const normalizar = (texto) =>
  String(texto || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

// Chips rápidos del mockup. `closed` es el añadido: sin él las convocatorias
// con el plazo cumplido (1.804 de 4.757) no tendrían forma de verse.
const CHIPS = [
  { id: 'all', label: 'Todas', test: (f) => !f._cerrada },
  { id: 'soon', label: 'Cierran pronto', test: (f) => !f._cerrada && f._fin !== null && f._fin <= 3 },
  { id: 'pay', label: 'Más de S/ 3,500', test: (f) => !f._cerrada && f._monto !== null && f._monto >= 3500 },
  { id: 'vac', label: 'Varias vacantes', test: (f) => !f._cerrada && f._vacantes > 1 },
  { id: 'closed', label: 'Plazo vencido', test: (f) => f._cerrada }
];

const SUELDOS = [
  { valor: '1500', label: 'Desde S/ 1,500' },
  { valor: '2500', label: 'Desde S/ 2,500' },
  { valor: '4000', label: 'Desde S/ 4,000' },
  { valor: '6000', label: 'Desde S/ 6,000' },
  { valor: '10000', label: 'Desde S/ 10,000' }
];

const ORDENES = [
  { valor: 'cierre_asc', label: 'Cierre más próximo' },
  { valor: 'cierre_desc', label: 'Cierre más lejano' },
  { valor: 'sueldo_desc', label: 'Mejor remuneración' },
  { valor: 'sueldo_asc', label: 'Menor remuneración' },
  { valor: 'vacantes_desc', label: 'Más vacantes' },
  { valor: 'entidad', label: 'Entidad (A–Z)' }
];

// El panel muestra siempre las cinco filas del perfil, numeradas como el
// mockup: lo que la convocatoria no pide se apaga en gris en vez de desaparecer.
const CAMPOS_PERFIL = [
  { campo: 'formacion', label: 'Formación' },
  { campo: 'experiencia', label: 'Experiencia' },
  { campo: 'especializacion', label: 'Especialización' },
  { campo: 'conocimiento', label: 'Conocimientos' },
  { campo: 'competencias', label: 'Competencias' }
];

// ==========================================================
// Descarga (vive en el módulo, no en el componente)
// ==========================================================
const cacheVigente = () =>
  Boolean(
    globalDataCache.data &&
      globalDataCache.timestamp &&
      Date.now() - globalDataCache.timestamp < CACHE_DURATION
  );

// Todo lo que la vista necesita, calculado una sola vez al descargar: filtrar y
// ordenar miles de filas en cada tecla no puede volver a parsear fechas, montos
// y textos de búsqueda.
const prepararFila = (row, indice) => {
  const inicioFecha = parseFecha(row.fecha_inicio);
  const finFecha = parseFecha(row.fecha_fin);
  const dias = diasHastaCierre(finFecha);
  const { regimen, modalidad, claseRegimen } = datosConvocatoria(row.numero_convocatoria);
  const ubic = partesUbicacion(row.ubicacion);
  const urgencia = urgenciaDe(dias);

  return {
    ...row,
    // La tabla no tiene clave primaria: la clave de React se arma con la
    // convocatoria, el puesto y la posición (dos puestos iguales en la misma
    // convocatoria existen y no deben colapsar en una sola fila).
    _key: `${row.numero_convocatoria || 's/c'}|${row.puesto || 's/p'}|${indice}`,
    _inicioFecha: inicioFecha,
    _finFecha: finFecha,
    _fin: dias,
    _cerrada: dias !== null && dias < 0,
    _nivel: urgencia.nivel,
    _plazoLabel: urgencia.label,
    _avance: avancePlazo(inicioFecha, finFecha, dias),
    _monto: montoNumero(row.remuneracion),
    _vacantes: Number(row.cantidad_vacantes) || 0,
    _regimen: regimen,
    _modalidad: modalidad,
    _claseRegimen: claseRegimen,
    _departamento: ubic.departamento,
    _detalleUbic: ubic.detalle,
    _titulo: capitalizar(row.puesto) || 'Puesto sin nombre',
    _entidad: capitalizar(row.entidad) || 'Entidad no indicada',
    // El logo se busca por la clave normalizada, no por el nombre literal: el
    // scraper lo cambia entre cargas. Ver `services/entidadLogos.js`.
    _claveEntidad: normalizarClave(row.entidad),
    _buscable: normalizar(
      [row.puesto, row.entidad, row.ubicacion, row.numero_convocatoria, row.codigo_oferta]
        .filter(Boolean)
        .join(' ')
    )
  };
};

const descargarTodo = async () => {
  const { count, error: errConteo } = await supabase
    .from(TABLA)
    .select(COLUMNAS_LISTA, { count: 'exact', head: true });
  if (errConteo) throw errConteo;
  if (!count) return [];

  const crudas = [];
  const lotes = Math.max(1, Math.ceil(count / BATCH_SIZE));
  for (let i = 0; i < lotes; i++) {
    let consulta = supabase.from(TABLA).select(COLUMNAS_LISTA);
    ORDEN_ESTABLE.forEach((columna) => {
      consulta = consulta.order(columna, { ascending: true, nullsFirst: true });
    });
    const { data, error: errLote } = await consulta.range(
      i * BATCH_SIZE,
      Math.min((i + 1) * BATCH_SIZE - 1, count - 1)
    );
    if (errLote) throw errLote;
    if (data?.length) crudas.push(...data);
  }

  return crudas.map(prepararFila);
};

// Devuelve la descarga en curso si ya hay una: dos montajes simultáneos (salir
// de la sección y volver mientras baja) comparten la misma promesa y los dos
// reciben el resultado.
const obtenerDatos = (forzar = false) => {
  if (!forzar && cacheVigente()) return Promise.resolve(globalDataCache.data);
  if (!forzar && globalDataCache.promesa) return globalDataCache.promesa;

  const promesa = descargarTodo()
    .then((datos) => {
      globalDataCache.data = datos;
      globalDataCache.timestamp = Date.now();
      globalDataCache.promesa = null;
      return datos;
    })
    .catch((e) => {
      // Sin esto, un fallo de red dejaría la promesa fallida en la caché y
      // todos los reintentos volverían a fallar sin tocar la red.
      globalDataCache.promesa = null;
      throw e;
    });

  globalDataCache.promesa = promesa;
  return promesa;
};

// Los campos del perfil no vienen en el listado (son el grueso del peso): se
// piden al abrir el panel. Sin clave primaria, la fila se localiza por su
// código de oferta y, cuando no lo trae, por convocatoria + puesto.
const cargarPerfil = async (fila) => {
  let consulta = supabase.from(TABLA).select(COLUMNAS_PERFIL);
  if (fila.codigo_oferta) {
    consulta = consulta.eq('codigo_oferta', fila.codigo_oferta);
  } else {
    consulta = consulta
      .eq('numero_convocatoria', fila.numero_convocatoria || '')
      .eq('puesto', fila.puesto || '');
  }
  const { data, error } = await consulta.limit(1);
  if (error) throw error;
  return data?.[0] || null;
};

// ==========================================================
// Acciones compartidas por la fila y el panel
// ==========================================================
const textoDeFila = (fila) => [
  fila._titulo,
  fila._entidad,
  `Convocatoria: ${fila.numero_convocatoria || 'No indicada'}`,
  `Remuneración: ${fila._monto !== null ? fmtSoles(fila._monto) : fila.remuneracion || 'No indicada'}`,
  `Postulación: ${fmtFecha(fila._inicioFecha)} al ${fmtFecha(fila._finFecha)}`
].join('\n');

const copiarFila = (fila) => {
  navigator.clipboard?.writeText(textoDeFila(fila))
    .then(() => toast.success('Convocatoria copiada', { autoClose: 1600 }))
    .catch(() => toast.error('No se pudo copiar'));
};

// `window.open` tiene que salir dentro del propio clic: después de un `await`
// el navegador lo bloquea.
const compartirFila = (fila) => {
  const enlace = enlaceDeBases(fila.enlace_detalle);
  const lineas = [
    '*Convocatoria de empleo público*',
    '',
    `*Puesto:* ${fila._titulo}`,
    `*Entidad:* ${fila._entidad}`,
    `*Convocatoria:* ${fila.numero_convocatoria || 'No indicada'}`,
    `*Ubicación:* ${[fila._departamento, fila._detalleUbic].filter(Boolean).join(' · ') || 'No indicada'}`,
    `*Vacantes:* ${fila._vacantes || 'No indicadas'}`,
    `*Remuneración:* ${fila._monto !== null ? fmtSoles(fila._monto) : fila.remuneracion || 'No indicada'}`,
    `*Postulación:* ${fmtFecha(fila._inicioFecha)} al ${fmtFecha(fila._finFecha)}`
  ];
  if (enlace) lineas.push('', `*Convocatoria:* ${enlace}`);
  lineas.push('', 'Compartido desde LicitaHUB');
  window.open(whatsappShareUrl(lineas.join('\n')), '_blank', 'noopener,noreferrer');
};

const abrirOficial = (fila) => {
  const enlace = enlaceDeBases(fila.enlace_detalle);
  if (!enlace) {
    toast.info('Esta convocatoria no trae enlace: búscala por su número en el portal de la entidad.');
    return;
  }
  window.open(enlace, '_blank', 'noopener,noreferrer');
};

// ==========================================================
// Desplegable de filtro (Departamento / Régimen / Remuneración)
// ==========================================================
const SelectChip = ({ etiqueta, valor, opciones, onElegir }) => {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef(null);

  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => {
      if (caja.current && !caja.current.contains(e.target)) setAbierto(false);
    };
    const escape = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', escape);
    };
  }, [abierto]);

  const elegida = opciones.find((o) => o.valor === valor);

  return (
    <div className={`te35-select ${valor ? 'is-activa' : ''}`} ref={caja}>
      <button
        type="button"
        className="te35-select__boton"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-label={etiqueta}
      >
        {elegida ? elegida.label : etiqueta}
        {valor ? (
          <span
            className="te35-select__quitar"
            role="button"
            tabIndex={-1}
            title={`Quitar el filtro de ${etiqueta.toLowerCase()}`}
            onClick={(e) => { e.stopPropagation(); onElegir(''); setAbierto(false); }}
          >
            <X size={11} strokeWidth={3} />
          </span>
        ) : (
          <ChevronDown size={13} strokeWidth={2.5} />
        )}
      </button>

      {abierto && (
        <div className="te35-select__menu" role="listbox">
          {opciones.length === 0 ? (
            <button type="button" className="te35-select__opcion" disabled>
              <span>Sin opciones</span>
            </button>
          ) : (
            opciones.map((o) => (
              <button
                type="button"
                key={o.valor}
                className={`te35-select__opcion ${o.valor === valor ? 'is-elegida' : ''}`}
                onClick={() => { onElegir(o.valor); setAbierto(false); }}
                role="option"
                aria-selected={o.valor === valor}
              >
                <span>{o.label}</span>
                {o.count !== undefined && <span>{fmtMiles(o.count)}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ==========================================================
// Panel de detalle
// ==========================================================
const PanelDetalle = ({ fila, onCerrar }) => {
  const [perfil, setPerfil] = useState(null);
  const [cargandoPerfil, setCargandoPerfil] = useState(false);

  const codigo = fila?.codigo_oferta;
  const convocatoria = fila?.numero_convocatoria;
  const nombrePuesto = fila?.puesto;

  useEffect(() => {
    if (!convocatoria && !codigo && !nombrePuesto) return undefined;

    let vivo = true;
    setPerfil(null);
    setCargandoPerfil(true);
    cargarPerfil({ codigo_oferta: codigo, numero_convocatoria: convocatoria, puesto: nombrePuesto })
      .then((datos) => { if (vivo) setPerfil(datos); })
      .catch((e) => {
        console.error('No se pudo leer el perfil del puesto:', e);
        if (vivo) setPerfil(null);
      })
      .finally(() => { if (vivo) setCargandoPerfil(false); });

    return () => { vivo = false; };
  }, [codigo, convocatoria, nombrePuesto]);

  useEffect(() => {
    const escape = (e) => { if (e.key === 'Escape') onCerrar(); };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [onCerrar]);

  const hayPerfil = CAMPOS_PERFIL.some(({ campo }) => String(perfil?.[campo] || '').trim());

  return (
    <>
      <button
        type="button"
        className="te35-overlay"
        onClick={onCerrar}
        aria-label="Cerrar el detalle"
      />
      <aside className="te35-drawer" role="dialog" aria-label={fila._titulo}>
        <div className="te35-drawer__head">
          <div className="te35-drawer__halo" aria-hidden="true" />

          <div className="te35-drawer__meta">
            <span>
              Conv. {fila.numero_convocatoria || '—'}
              {fila.codigo_oferta ? ` · Oferta ${fila.codigo_oferta}` : ''}
            </span>
            <button type="button" className="te35-drawer__cerrar" onClick={onCerrar} title="Cerrar">
              <X size={16} />
            </button>
          </div>

          <h2 className="te35-drawer__titulo">{fila._titulo}</h2>
          <div className="te35-drawer__entidad">{fila._entidad}</div>

          <div className="te35-drawer__chips">
            {fila._regimen && <span className="te35-drawer__chip">{fila._regimen}</span>}
            {fila._modalidad && <span className="te35-drawer__chip">{fila._modalidad}</span>}
            <span className="te35-drawer__chip te35-drawer__chip--plazo">{fila._plazoLabel}</span>
          </div>
        </div>

        <div className="te35-drawer__datos">
          <dl className="te35-drawer__tarjetas">
            <div className="te35-drawer__tarjeta te35-drawer__tarjeta--pago">
              <dt>Remuneración</dt>
              <dd>{fila._monto !== null ? fmtSoles(fila._monto) : fila.remuneracion || '—'}</dd>
            </div>
            <div className="te35-drawer__tarjeta te35-drawer__tarjeta--ubic">
              <dt>Ubicación</dt>
              <dd>
                {capitalizar(fila._departamento) || '—'}
                {fila._detalleUbic ? ` · ${capitalizar(fila._detalleUbic)}` : ''}
              </dd>
            </div>
            <div className="te35-drawer__tarjeta te35-drawer__tarjeta--vac">
              <dt>Vacantes</dt>
              <dd>{fila._vacantes || '—'}</dd>
            </div>
          </dl>

          <div className={`te35-drawer__linea is-${fila._nivel}`}>
            <div className="te35-drawer__linea-textos">
              <span>Inicio <b>{fmtDiaMes(fila._inicioFecha)}</b></span>
              <span>Cierre <b>{fmtDiaMes(fila._finFecha)}</b></span>
            </div>
            <div className="te35-drawer__linea-barra">
              <div className="te35-drawer__linea-fill" style={{ width: `${fila._avance}%` }} />
              <div className="te35-drawer__linea-punto" style={{ left: `${fila._avance}%` }} />
            </div>
          </div>
        </div>

        <div className="te35-drawer__cuerpo">
          <div className="te35-drawer__rotulo">PERFIL DEL PUESTO</div>

          {cargandoPerfil ? (
            <div className="te35-cargando te35-cargando--perfil">
              {[90, 70, 80].map((ancho) => (
                <div className="te35-cargando__fila" key={ancho} style={{ width: `${ancho}%` }} />
              ))}
            </div>
          ) : hayPerfil ? (
            <div className="te35-perfil">
              {CAMPOS_PERFIL.map(({ campo, label }, i) => {
                const texto = String(perfil?.[campo] || '').trim();
                return (
                  <div className="te35-perfil__item" key={campo}>
                    <span className={`te35-perfil__num pal-${i}`}>{i + 1}</span>
                    <div>
                      <div className={`te35-perfil__clave pal-${i}`}>{label}</div>
                      <p className={`te35-perfil__valor ${texto ? '' : 'is-vacio'}`}>
                        {texto || 'No aplica'}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="te35-perfil__aviso">
              Esta convocatoria todavía no trae el perfil detallado (formación, experiencia y
              competencias). Los requisitos completos están en las bases que publica la entidad
              con el número de convocatoria.
            </p>
          )}
        </div>

        <div className="te35-drawer__pie">
          <button type="button" className="te35-btn te35-btn--copiar" onClick={() => copiarFila(fila)}>
            <Copy size={14} />
            Copiar
          </button>
          <button type="button" className="te35-btn te35-btn--wsp" onClick={() => compartirFila(fila)}>
            <Share2 size={14} />
            Compartir
          </button>
          <button
            type="button"
            className="te35-btn te35-btn--oficial"
            onClick={() => abrirOficial(fila)}
          >
            Ver convocatoria oficial
            <ExternalLink size={15} />
          </button>
        </div>
      </aside>
    </>
  );
};

// ==========================================================
// Fila de la tabla
// ==========================================================
// Es un `div` con rol de botón y no un `<button>`: dentro van los tres botones
// de acción, y anidar botones no es HTML válido.
const FilaTabla = ({ fila, logoUrl, seleccionada, onAbrir }) => (
  <div
    className={`te35-tabla__fila ${seleccionada ? 'is-sel' : ''} ${fila._cerrada ? 'is-cerrada' : ''}`}
    role="button"
    tabIndex={0}
    onClick={() => onAbrir(fila)}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onAbrir(fila);
      }
    }}
  >
    <div className="te35-puesto">
      <LogoEntidad nombre={fila.entidad} url={logoUrl} tamano={LADO_LOGO} />
      <div className="te35-puesto__texto">
        <div className="te35-puesto__linea">
          <span className="te35-puesto__nombre">{fila._titulo}</span>
          {fila._regimen && (
            <span className={`te35-regimen reg-${fila._claseRegimen}`}>{fila._regimen}</span>
          )}
        </div>
        <div className="te35-puesto__entidad">{fila._entidad}</div>
      </div>
    </div>

    <div className="te35-ubic">
      <MapPin size={14} strokeWidth={2.2} />
      <div className="te35-ubic__texto">
        <div className="te35-ubic__dpto">{capitalizar(fila._departamento) || '—'}</div>
        {fila._detalleUbic && (
          <div className="te35-ubic__prov">{capitalizar(fila._detalleUbic)}</div>
        )}
      </div>
    </div>

    <div className="te35-vac">
      <span>{fila._vacantes || '—'}</span>
    </div>

    <div className="te35-pago">
      <div className="te35-pago__monto">{fila._monto !== null ? fmtSoles(fila._monto) : '—'}</div>
      <div className="te35-pago__nota">mensual</div>
    </div>

    <div className={`te35-plazo is-${fila._nivel}`}>
      <div className="te35-plazo__top">
        <span className="te35-plazo__pill">
          <span className="te35-plazo__punto" />
          {fila._plazoLabel}
        </span>
        <span className="te35-plazo__rango">
          {fmtDiaMes(fila._inicioFecha)} – {fmtDiaMes(fila._finFecha)}
        </span>
      </div>
      <div className="te35-plazo__barra">
        <div className="te35-plazo__fill" style={{ width: `${fila._avance}%` }} />
      </div>
    </div>

    <div className="te35-acciones">
      <button
        type="button"
        className="te35-accion te35-accion--copiar"
        title="Copiar la convocatoria"
        onClick={(e) => { e.stopPropagation(); copiarFila(fila); }}
      >
        <Copy size={14} />
      </button>
      <button
        type="button"
        className="te35-accion te35-accion--wsp"
        title="Compartir por WhatsApp"
        onClick={(e) => { e.stopPropagation(); compartirFila(fila); }}
      >
        <Share2 size={14} />
      </button>
      <button
        type="button"
        className="te35-accion te35-accion--abrir"
        title="Abrir la convocatoria oficial"
        onClick={(e) => { e.stopPropagation(); abrirOficial(fila); }}
      >
        <ExternalLink size={14} />
      </button>
    </div>
  </div>
);

// ==========================================================
// Página
// ==========================================================
const TrabajaEstadoPage = () => {
  const [filas, setFilas] = useState([]);
  // clave normalizada de la entidad -> URL de su logo
  const [logos, setLogos] = useState(() => new Map());
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [actualizado, setActualizado] = useState(null);

  const [busqueda, setBusqueda] = useState('');
  const [chip, setChip] = useState('all');
  const [departamento, setDepartamento] = useState('');
  const [regimen, setRegimen] = useState('');
  const [sueldoMin, setSueldoMin] = useState('');
  const [orden, setOrden] = useState('cierre_asc');
  const [pagina, setPagina] = useState(1);
  const [seleccion, setSeleccion] = useState(null);

  const inicializado = useRef(false);
  // La descarga tarda segundos: sin esto, salir de la sección a mitad de la
  // carga deja un setState sobre un componente ya desmontado.
  const montado = useRef(true);
  const inputBusqueda = useRef(null);

  // --- Carga ---
  const cargar = useCallback(async (forzar = false) => {
    setCargando(true);
    setError(null);
    try {
      // Dos consultas independientes: no tiene sentido encadenarlas, y si los
      // logos fallan el listado se pinta igual (el servicio devuelve un mapa
      // vacío y las filas caen al icono por tipo).
      const [datos, mapaLogos] = await Promise.all([
        obtenerDatos(forzar),
        obtenerLogos(forzar)
      ]);
      if (!montado.current) return;
      setFilas(datos);
      setLogos(mapaLogos);
      setActualizado(new Date(globalDataCache.timestamp));
    } catch (e) {
      console.error('Error al cargar las convocatorias de empleo público:', e);
      if (!montado.current) return;
      setError(
        e?.code === '42P01'
          ? `La tabla «${TABLA}» no existe en Supabase.`
          : 'No se pudieron cargar las convocatorias. Vuelve a intentarlo en un momento.'
      );
    } finally {
      if (montado.current) setCargando(false);
    }
  }, []);

  useEffect(() => {
    montado.current = true;
    return () => { montado.current = false; };
  }, []);

  useEffect(() => {
    if (inicializado.current) return;
    inicializado.current = true;

    if (cacheVigente()) {
      setFilas(globalDataCache.data);
      setActualizado(new Date(globalDataCache.timestamp));
      setCargando(false);
      obtenerLogos().then((mapa) => { if (montado.current) setLogos(mapa); });
      return;
    }
    cargar();
  }, [cargar]);

  // El atajo «/» del mockup: enfoca el buscador desde cualquier parte, salvo
  // cuando ya se está escribiendo en un campo.
  useEffect(() => {
    const alPulsar = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      const etiqueta = document.activeElement?.tagName?.toLowerCase();
      if (etiqueta === 'input' || etiqueta === 'textarea' || etiqueta === 'select') return;
      e.preventDefault();
      inputBusqueda.current?.focus();
    };
    document.addEventListener('keydown', alPulsar);
    return () => document.removeEventListener('keydown', alPulsar);
  }, []);

  // --- Lo que el usuario buscó, aplicado a todo menos al chip activo ---
  // Los conteos de los chips tienen que contar sobre el mismo universo que el
  // listado; si no, el número prometido no coincide con lo que sale al pulsar.
  const baseFiltrada = useMemo(() => {
    const texto = normalizar(busqueda.trim());
    const minimo = Number(sueldoMin) || 0;

    return filas.filter((fila) => {
      if (departamento && fila._departamento !== departamento) return false;
      if (regimen && fila._regimen !== regimen) return false;
      if (minimo > 0 && (fila._monto === null || fila._monto < minimo)) return false;
      if (texto && !fila._buscable.includes(texto)) return false;
      return true;
    });
  }, [filas, busqueda, departamento, regimen, sueldoMin]);

  const conteosChips = useMemo(() => {
    const conteo = {};
    CHIPS.forEach(({ id, test }) => {
      conteo[id] = baseFiltrada.reduce((n, fila) => n + (test(fila) ? 1 : 0), 0);
    });
    return conteo;
  }, [baseFiltrada]);

  const filtradas = useMemo(() => {
    const activo = CHIPS.find((c) => c.id === chip) || CHIPS[0];
    return baseFiltrada.filter(activo.test);
  }, [baseFiltrada, chip]);

  // --- Orden ---
  const ordenadas = useMemo(() => {
    const lista = [...filtradas];
    const porCierre = (a, b, dir) => {
      // Sin fecha de cierre van al final en los dos sentidos.
      if (a._fin === null && b._fin === null) return 0;
      if (a._fin === null) return 1;
      if (b._fin === null) return -1;
      return dir * (a._fin - b._fin);
    };
    const porMonto = (a, b, dir) => {
      if (a._monto === null && b._monto === null) return 0;
      if (a._monto === null) return 1;
      if (b._monto === null) return -1;
      return dir * (a._monto - b._monto);
    };

    switch (orden) {
      case 'cierre_desc': return lista.sort((a, b) => porCierre(a, b, -1));
      case 'sueldo_desc': return lista.sort((a, b) => porMonto(a, b, -1));
      case 'sueldo_asc': return lista.sort((a, b) => porMonto(a, b, 1));
      case 'vacantes_desc': return lista.sort((a, b) => b._vacantes - a._vacantes || porCierre(a, b, 1));
      case 'entidad': return lista.sort((a, b) => String(a.entidad || '').localeCompare(String(b.entidad || ''), 'es'));
      default:
        // Las abiertas primero y, dentro de ellas, la que cierra antes.
        return lista.sort((a, b) => {
          if (a._cerrada !== b._cerrada) return a._cerrada ? 1 : -1;
          return porCierre(a, b, 1);
        });
    }
  }, [filtradas, orden]);

  // --- Opciones de los desplegables ---
  const opcionesDepartamento = useMemo(() => {
    const conteo = new Map();
    filas.forEach((f) => {
      if (!f._departamento) return;
      conteo.set(f._departamento, (conteo.get(f._departamento) || 0) + 1);
    });
    return Array.from(conteo.entries())
      .sort((a, b) => a[0].localeCompare(b[0], 'es'))
      .map(([valor, count]) => ({ valor, label: capitalizar(valor), count }));
  }, [filas]);

  const opcionesRegimen = useMemo(() => {
    const conteo = new Map();
    filas.forEach((f) => {
      if (!f._regimen) return;
      conteo.set(f._regimen, (conteo.get(f._regimen) || 0) + 1);
    });
    return Array.from(conteo.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([valor, count]) => ({ valor, label: valor, count }));
  }, [filas]);

  // --- Métricas del hero ---
  const metricas = useMemo(() => {
    const abiertas = filas.filter((f) => !f._cerrada);
    const montos = abiertas.map((f) => f._monto).filter((m) => m !== null);
    return {
      total: filas.length,
      abiertas: abiertas.length,
      vacantes: abiertas.reduce((suma, f) => suma + f._vacantes, 0),
      entidades: new Set(abiertas.map((f) => f.entidad).filter(Boolean)).size,
      cierranPronto: abiertas.filter((f) => f._fin !== null && f._fin <= 3).length,
      mediana: mediana(montos),
      maximo: montos.length ? Math.max(...montos) : null
    };
  }, [filas]);

  // Cualquier cambio de filtro vuelve a la primera página: quedarse en la 4 de
  // un listado que ahora tiene 2 páginas mostraba una tabla vacía.
  useEffect(() => {
    setPagina(1);
  }, [busqueda, chip, departamento, regimen, sueldoMin, orden]);

  const totalPaginas = Math.max(1, Math.ceil(ordenadas.length / POR_PAGINA));
  const paginaActual = Math.min(pagina, totalPaginas);
  const desde = (paginaActual - 1) * POR_PAGINA;
  const visibles = ordenadas.slice(desde, desde + POR_PAGINA);

  const hayFiltros = Boolean(busqueda.trim() || departamento || regimen || sueldoMin || chip !== 'all');

  const limpiarFiltros = () => {
    setBusqueda('');
    setChip('all');
    setDepartamento('');
    setRegimen('');
    setSueldoMin('');
  };

  return (
    <div className="te35">
      <ToastContainer position="bottom-right" newestOnTop theme="light" />

      {/* Hero */}
      <section className="te35-hero">
        <div className="te35-hero__trama" aria-hidden="true" />
        <div className="te35-hero__halo" aria-hidden="true" />

        <div className="te35-hero__top">
          <div className="te35-hero__text">
            <div className="te35-hero__rotulos">
              <span className="te35-hero__badge">EMPLEO PÚBLICO</span>
              {actualizado && (
                <span className="te35-hero__sello">
                  <span className="te35-hero__punto" />
                  Actualizado hoy{' '}
                  {actualizado.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false })}
                </span>
              )}
            </div>

            <h1 className="te35-hero__title">Trabaja para el Estado</h1>
            <p className="te35-hero__sub">
              Convocatorias activas de {fmtMiles(metricas.entidades)} instituciones públicas.
              Filtra, compara y revisa el perfil antes de postular.
            </p>
          </div>

          <div className="te35-hero__acciones">
            <label className="te35-buscador">
              <Search size={17} strokeWidth={2.2} />
              <input
                ref={inputBusqueda}
                className="te35-buscador__input"
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar puesto, entidad o N° de convocatoria"
                aria-label="Buscar convocatorias de empleo público"
              />
              {busqueda ? (
                <button
                  type="button"
                  className="te35-buscador__limpiar"
                  onClick={() => setBusqueda('')}
                  title="Limpiar búsqueda"
                >
                  <X size={14} />
                </button>
              ) : (
                <span className="te35-buscador__atajo" aria-hidden="true">/</span>
              )}
            </label>

            <button
              type="button"
              className="te35-hero__btn"
              onClick={() => cargar(true)}
              disabled={cargando}
            >
              <RefreshCw size={15} className={cargando ? 'te35-girar' : undefined} />
              <span className="te35-ancho">Actualizar</span>
            </button>
          </div>
        </div>

        {/* KPI dentro del hero */}
        <div className="te35-kpis">
          <div className="te35-kpi te35-kpi--abierta">
            <span className="te35-kpi__icono" aria-hidden="true"><Target size={18} /></span>
            <div className="te35-kpi__cuerpo">
              <div className="te35-kpi__label">Postulación abierta</div>
              <div className="te35-kpi__valor">{fmtMiles(metricas.abiertas)}</div>
              <div className="te35-kpi__sub">de {fmtMiles(metricas.total)} publicadas</div>
            </div>
          </div>

          <div className="te35-kpi te35-kpi--vacantes">
            <span className="te35-kpi__icono" aria-hidden="true"><Users size={18} /></span>
            <div className="te35-kpi__cuerpo">
              <div className="te35-kpi__label">Vacantes disponibles</div>
              <div className="te35-kpi__valor">{fmtMiles(metricas.vacantes)}</div>
              <div className="te35-kpi__sub">en {fmtMiles(metricas.entidades)} entidades</div>
            </div>
          </div>

          <div className="te35-kpi te35-kpi--urgente">
            <span className="te35-kpi__icono" aria-hidden="true"><AlertTriangle size={17} /></span>
            <div className="te35-kpi__cuerpo">
              <div className="te35-kpi__label">Cierran en 3 días</div>
              <div className="te35-kpi__valor">{fmtMiles(metricas.cierranPronto)}</div>
              <div className="te35-kpi__sub">prioriza estas</div>
            </div>
          </div>

          {/* La mediana manda y el máximo va de pista: hay convocatorias con el
              monto mal cargado en origen («S/. 836,419.00» es «S/. 8,364.19»
              con el punto decimal perdido) y de titular hacían dudar de todo. */}
          <div className="te35-kpi te35-kpi--pago">
            <span className="te35-kpi__icono" aria-hidden="true">S/</span>
            <div className="te35-kpi__cuerpo">
              <div className="te35-kpi__label">Remuneración mediana</div>
              <div className="te35-kpi__valor">{fmtSolesCorto(metricas.mediana)}</div>
              <div className="te35-kpi__sub">
                {metricas.maximo !== null ? `máx. ${fmtSolesCorto(metricas.maximo)}` : 'sin montos'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {error && (
        <div className="te35-alerta">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Filtros */}
      <div className="te35-filtros">
        {/* En escritorio este contenedor es `display: contents` y no altera la
            barra; en móvil se vuelve una tira deslizable para no apilar cinco
            chips en tres líneas. */}
        <div className="te35-filtros__chips">
          {CHIPS.map((c) => (
            <button
              type="button"
              key={c.id}
              className={`te35-chip te35-chip--${c.id} ${chip === c.id ? 'is-activa' : ''}`}
              onClick={() => setChip(c.id)}
              aria-pressed={chip === c.id}
            >
              <span className="te35-chip__punto" />
              {c.label}
              <span className="te35-chip__count">{fmtMiles(conteosChips[c.id] || 0)}</span>
            </button>
          ))}
        </div>

        <span className="te35-filtros__sep" />

        <SelectChip
          etiqueta="Departamento"
          valor={departamento}
          opciones={opcionesDepartamento}
          onElegir={setDepartamento}
        />
        <SelectChip
          etiqueta="Régimen"
          valor={regimen}
          opciones={opcionesRegimen}
          onElegir={setRegimen}
        />
        <SelectChip
          etiqueta="Remuneración"
          valor={sueldoMin}
          opciones={SUELDOS}
          onElegir={setSueldoMin}
        />

        {hayFiltros && (
          <button type="button" className="te35-btn te35-btn--chico" onClick={limpiarFiltros}>
            <X size={13} />
            Limpiar
          </button>
        )}

        <div className="te35-orden">
          <label htmlFor="te35-orden">Ordenar por</label>
          <select
            id="te35-orden"
            className="te35-orden__select"
            value={orden}
            onChange={(e) => setOrden(e.target.value)}
          >
            {ORDENES.map((o) => (
              <option key={o.valor} value={o.valor}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Listado */}
      {cargando ? (
        <div className="te35-cargando">
          {Array.from({ length: 8 }).map((_, i) => (
            <div className="te35-cargando__fila" key={i} style={{ width: `${100 - (i % 4) * 11}%` }} />
          ))}
        </div>
      ) : visibles.length === 0 ? (
        <div className="te35-vacio">
          <div className="te35-vacio__icono">
            <Briefcase size={22} />
          </div>
          <h3 className="te35-vacio__titulo">
            {metricas.total === 0
              ? 'Todavía no hay convocatorias cargadas'
              : chip === 'all' && metricas.abiertas === 0
                ? 'Ninguna convocatoria tiene la postulación abierta'
                : 'Ningún puesto coincide con los filtros'}
          </h3>
          <p className="te35-vacio__texto">
            {metricas.total === 0
              ? 'En cuanto se publiquen las convocatorias de empleo público aparecerán acá, con su plazo de postulación y el perfil del puesto.'
              : chip === 'all' && metricas.abiertas === 0
                ? `Las ${fmtMiles(metricas.total)} convocatorias publicadas ya cerraron su plazo. Puedes revisarlas para ver qué perfiles pide cada entidad.`
                : 'Prueba con otro departamento, baja el monto mínimo o quita los filtros.'}
          </p>
          {metricas.total > 0 && (
            <div className="te35-vacio__acciones">
              {chip === 'all' && metricas.abiertas === 0 ? (
                <button
                  type="button"
                  className="te35-btn te35-btn--oficial te35-btn--chico"
                  onClick={() => setChip('closed')}
                >
                  Ver las convocatorias vencidas
                </button>
              ) : (
                <button
                  type="button"
                  className="te35-btn te35-btn--oficial te35-btn--chico"
                  onClick={limpiarFiltros}
                >
                  Quitar los filtros
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="te35-tabla-bloque">
          <div className="te35-tabla">
            <div className="te35-tabla__head">
              <span>PUESTO Y ENTIDAD</span>
              <span>UBICACIÓN</span>
              <span className="is-centro">VAC.</span>
              <span className="is-derecha">REMUNERACIÓN</span>
              <span>PLAZO DE POSTULACIÓN</span>
              <span className="is-derecha">ACCIONES</span>
            </div>

            {visibles.map((fila) => (
              <FilaTabla
                key={fila._key}
                fila={fila}
                logoUrl={logos.get(fila._claveEntidad)}
                seleccionada={seleccion?._key === fila._key}
                onAbrir={setSeleccion}
              />
            ))}

            <div className="te35-tabla__pie">
              <span>
                Mostrando <strong>{fmtMiles(desde + 1)}</strong>–
                <strong>{fmtMiles(Math.min(desde + POR_PAGINA, ordenadas.length))}</strong> de{' '}
                <strong>{fmtMiles(ordenadas.length)}</strong> convocatorias
              </span>
              {totalPaginas > 1 && (
                <span className="te35-tabla__paginas">
                  <button
                    type="button"
                    className="te35-btn te35-btn--pagina"
                    onClick={() => setPagina((p) => Math.max(1, p - 1))}
                    disabled={paginaActual === 1}
                  >
                    <ChevronLeft size={14} />
                    Anterior
                  </button>
                  <button
                    type="button"
                    className="te35-btn te35-btn--pagina te35-btn--pagina-activa"
                    onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                    disabled={paginaActual === totalPaginas}
                  >
                    Siguiente
                    <ChevronRight size={14} />
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {seleccion && <PanelDetalle fila={seleccion} onCerrar={() => setSeleccion(null)} />}
    </div>
  );
};

export default TrabajaEstadoPage;
